/**
 * 受信した位置の異常値（GPSの飛び）を弾く（docs/08 §229）。
 *
 * 2026-11-01、クルーの端末（T_saori、地上・時速0.4km）に高度 4528ft（約1380m）という異常な値が入り、
 * 気球ではないのに Blue PZ の天井超過の警告が出た。端末は地上にいるので、次の前提で弾く:
 *
 * - 気球・車は、決まった時間に動ける距離・上がれる高さに限りがある。前に採用した位置から、
 *   **その時間で動ける量（＋GPSのゆらぎ）を超える跳び**は異常値とみなす
 * - 位置（緯度経度）の異常は、その点ごと捨てる。高度だけの異常は、点は残して**高度を空にする**
 *   （クルーの車の位置は地図に出し続けたい。高度を空にすると天井の判定にも使われない）
 * - 基準は「最後に採用した値」。異常値が続いても、基準からの時間が延びるぶん許す量が増えるので、
 *   本当に動いた（飛んだ）端末は、時間が経てば受け入れられる
 * - 最初の1点が異常だったときに、あとの正しい値を弾き続けないよう、連続で一定数
 *   はじいたら、その値を新しい基準にする（`reseedAfter`）
 *
 * ★ 限界: 異常値が**続く間は物理的にあり得る範囲に収まる速さで**ずれていくと、見分けられない
 *   （たとえば地上の端末の高度が、数分かけてゆっくり1000m上がるなど）。そこまでは弾かない。
 */

import type { FixSample } from './types.ts';

export interface SanityLimits {
  /** 水平の最大速度 [m/s]（気球は〜30、チェイスカーは〜40。余裕を見て） */
  maxHorizontalSpeedMps: number;
  /** 鉛直の最大速度 [m/s]（気球の上昇・下降は〜5、急降下でも〜10） */
  maxVerticalSpeedMps: number;
  /** 位置のゆらぎ [m]（GPSの誤差・古い位置の割り込みなど） */
  positionNoiseM: number;
  /** 高度のゆらぎ [m]（GPSの高度は水平より誤差が大きい） */
  altitudeNoiseM: number;
  /** 高度の絶対の範囲 [m]（これを外れたら無条件に異常） */
  minAltM: number;
  maxAltM: number;
  /** 連続でこの数はじいたら、その値を新しい基準にする（最初の1点が異常だったときの救済） */
  reseedAfter: number;
}

export const DEFAULT_SANITY_LIMITS: SanityLimits = {
  maxHorizontalSpeedMps: 70,
  maxVerticalSpeedMps: 8,
  positionNoiseM: 100,
  altitudeNoiseM: 30,
  minAltM: -500,
  maxAltM: 15000,
  reseedAfter: 10,
};

export interface SanitizeResult {
  /** 異常値を除いた（高度だけ異常なものは高度を空にした）点。時刻順は入力のまま */
  fixes: FixSample[];
  /** 位置が異常で捨てた点の数 */
  droppedPositions: number;
  /** 高度が異常で、高度を空にした点の数 */
  clearedAltitudes: number;
}

const M_PER_DEG_LAT = 111_320;

/** 近距離の2点間の距離 [m]（等距円筒。数十km以内の判定に十分） */
function approxDistanceM(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const dLat = (b.lat - a.lat) * M_PER_DEG_LAT;
  const dLon = (b.lon - a.lon) * M_PER_DEG_LAT * Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180));
  return Math.hypot(dLat, dLon);
}

function validPosition(f: { lat: number; lon: number }): boolean {
  return (
    Number.isFinite(f.lat) &&
    Number.isFinite(f.lon) &&
    Math.abs(f.lat) <= 90 &&
    Math.abs(f.lon) <= 180 &&
    !(f.lat === 0 && f.lon === 0) // 取れていないときの 0,0
  );
}

/** 1つの端末の点を時刻順に見て、異常値を弾く */
export function sanitizeFixes(
  fixes: readonly FixSample[],
  limits: SanityLimits = DEFAULT_SANITY_LIMITS,
): SanitizeResult {
  const out: FixSample[] = [];
  let droppedPositions = 0;
  let clearedAltitudes = 0;
  /** 最後に採用した位置・高度（それぞれの時刻つき） */
  let refPos: { lat: number; lon: number; t: number } | null = null;
  let refAlt: { altM: number; t: number } | null = null;
  let posRejects = 0;
  let altRejects = 0;

  for (const f of fixes) {
    // --- 位置 ---
    if (!validPosition(f)) {
      droppedPositions++;
      continue;
    }
    if (refPos) {
      const dt = Math.abs(f.t - refPos.t) / 1000;
      const allowed = limits.positionNoiseM + limits.maxHorizontalSpeedMps * dt;
      if (approxDistanceM(refPos, f) > allowed) {
        posRejects++;
        if (posRejects < limits.reseedAfter) {
          droppedPositions++;
          continue;
        }
        // 連続ではじきすぎ: こちらが新しい基準（最初の点が異常だった場合）
      }
    }
    posRejects = 0;
    refPos = { lat: f.lat, lon: f.lon, t: f.t };

    // --- 高度（気圧高度があればそれ、無ければ海抜。判定に使う順と同じ） ---
    const altM = f.altPressure ?? f.altMsl;
    if (altM == null || !Number.isFinite(altM)) {
      out.push(f);
      continue;
    }
    let bad = altM < limits.minAltM || altM > limits.maxAltM;
    if (!bad && refAlt) {
      const dt = Math.abs(f.t - refAlt.t) / 1000;
      bad = Math.abs(altM - refAlt.altM) > limits.altitudeNoiseM + limits.maxVerticalSpeedMps * dt;
    }
    if (bad) {
      altRejects++;
      // 絶対の範囲を外れたものは、救済（新しい基準）にしない
      const absolutelyBad = altM < limits.minAltM || altM > limits.maxAltM;
      if (absolutelyBad || altRejects < limits.reseedAfter) {
        clearedAltitudes++;
        out.push({ ...f, altMsl: null, altPressure: null });
        continue;
      }
    }
    altRejects = 0;
    refAlt = { altM, t: f.t };
    out.push(f);
  }
  return { fixes: out, droppedPositions, clearedAltitudes };
}
